import type { DocumentEntity } from 'services/documents/entities';
import type { FlexDataLink } from 'pages/flex-data/flex-data-links/types';
import { ProspectType } from 'services/repositories/interfaces/ProspectRepository';
import { isCrmCompositeId, toProposifyCompositeId } from 'services/company-and-contacts/mappers';
import type { ContactOption, DocumentSettingsFormValues } from './types';
import type { ProspectOption } from './useProspect';

export function constructInitialValues(document: DocumentEntity, assignedUserLegacyId: string | undefined): DocumentSettingsFormValues {
  let prospect: ProspectOption | null = null;
  let contact: ContactOption | null = null;

  if (document.prospect) {
    const { id, name, type, uid } = document.prospect;

    if (type === ProspectType.PERSON) {
      contact = {
        label: name,
        value: id,
      };
    } else {
      prospect = {
        label: name,
        value: uid,
        id,
        type,
      };

      if (type === ProspectType.COMPANY && document.prospect.contact) {
        contact = {
          label: document.prospect.contact.name,
          value: document.prospect.contact.id,
        };
      }
    }
  }

  const initialValues: DocumentSettingsFormValues = {
    prospect,
    contact,
    assignedUser: null,
  };

  if (document.assignedUser) {
    return {
      ...initialValues,
      assignedUser: {
        label: document.assignedUser.name,
        value: assignedUserLegacyId ?? document.assignedUser.id,
      },
    };
  }

  return initialValues;
}

// Legacy (pre-CRM-linking) documents store plain Proposify UUIDs on prospect/contact.
// The CRM composite id for a Proposify-sourced entity is deterministic: Proposify#{accountId}#{objectType}#{legacyId}.
// This lets the CRM company/contact dropdowns resolve the right option without a lookup.
export function withMigratedLegacyIds(
  initialValues: DocumentSettingsFormValues,
  accountId: string | undefined
): DocumentSettingsFormValues {
  if (!accountId) return initialValues;

  const prospect =
    initialValues.prospect && !isCrmCompositeId(initialValues.prospect.id)
      ? {
          ...initialValues.prospect,
          id: toProposifyCompositeId(accountId, 'Company', initialValues.prospect.id),
          value: toProposifyCompositeId(accountId, 'Company', initialValues.prospect.id),
        }
      : initialValues.prospect;

  const contact =
    initialValues.contact && !isCrmCompositeId(initialValues.contact.value)
      ? {
          ...initialValues.contact,
          value: toProposifyCompositeId(accountId, 'Client', initialValues.contact.value),
        }
      : initialValues.contact;

  return { ...initialValues, prospect, contact };
}

export function sortLinkedObjects(linkedObjects: FlexDataLink[]): FlexDataLink[] {
  return [...linkedObjects].sort((a, b) => {
    if (a.createdAt && b.createdAt) return b.createdAt.localeCompare(a.createdAt);
    if (a.createdAt) return -1;
    if (b.createdAt) return 1;
    return a.objectLabel.localeCompare(b.objectLabel);
  });
}
